import React, { useCallback, useState, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import Slider from 'react-slider';

import debounce from 'lodash/debounce';
import { gtmEventHandler } from '@common/utils/gtm';
import { GTMTracking } from '@common/ui/general';
import {
  PRE_SELECTED_FILTERS,
} from '@alumni_v2/data/preSelectedFilter';
import {
  requestParamsSelector,
} from '@alumni_v2/selectors/reviews';
import {
  updateRequestParams,
} from '@alumni_v2/stores/reviews/actions';

import doubleStarIcon from '@alumni_v2/images/svg/doubleStar.svg';
import powerIcon from '@alumni_v2/images/svg/powerIcon.svg';
// import LightningIcon from '@alumni_v2/images/svg/lightning-01.svg';
import setPreSelectedFilters from '@alumni_v2/utils/setPreSelectedFilters';

import styles from './QuickFilters.module.scss';

const DSML_QUICK_FILTER = 'data_science_&_ml';
const DSML_URL_PATH = '/data-science-course/reviews/';
const AIML_QUICK_FILTER = 'artificial_intelligence_&_ml';
const AIML_URL_PATH = '/ai-machine-learning-course/reviews/';
const excludeQuickFilters = ['Infosys', 'Tcs', 'Amazon', 'Wipro', 'Cognizant'];
const REVIEW_URL_PATH = '/review/';

function QuickFilters() {
  const dispatch = useDispatch();
  const { showAlumniConnect } = window.__INITIAL_STATE__;

  const extractKeywordsFromURL = () => {
    const urlParams = new URLSearchParams(window.location.search);
    const keywords = urlParams.getAll('keywords');

    return keywords?.length > 0 ? keywords : [];
  };

  const reconstructFiltersFromQuery = (queryString) => {
    const urlParams = new URLSearchParams(queryString);
    const advancedKeywords = urlParams.getAll('advanced_keyword');

    const filters = {};

    advancedKeywords.forEach(value => {
      const parts = value.split(',').map(decodeURIComponent);
      const filterKey = parts[0];

      if (!filters[filterKey]) {
        filters[filterKey] = [];
      }

      if (filterKey === 'experience' || filterKey === 'growth') {
        const filterObject = {};
        parts.slice(1).forEach(part => {
          const [key, val] = part.split('=');
          filterObject[key] = val;
        });
        filters[filterKey].push(filterObject);
      } else {
        // eslint-disable-next-line max-len
        const [filterKey1, filterValue] = value.split(',').map(decodeURIComponent);
        filters[filterKey1].push(filterValue);
      }
    });

    return filters;
  };


  const getPreSelectedFilters = () => {
    const preSelectedFilters = window.sessionStorage
      .getItem(PRE_SELECTED_FILTERS);
    const preSelectedFiltersJson = JSON.parse(preSelectedFilters) || [];
    const extractedKeywords = extractKeywordsFromURL();
    const filteredExtractedKeywords = extractedKeywords
      .filter(keyword => !preSelectedFiltersJson.includes(keyword));
    preSelectedFiltersJson.push(...filteredExtractedKeywords);

    if (window.location.pathname === DSML_URL_PATH) {
      if (!preSelectedFiltersJson.includes(DSML_QUICK_FILTER)) {
        preSelectedFiltersJson.push(DSML_QUICK_FILTER);
      }
    } else if (window.location.pathname === AIML_URL_PATH) {
      if (!preSelectedFiltersJson.includes(AIML_QUICK_FILTER)) {
        preSelectedFiltersJson.push(AIML_QUICK_FILTER);
      }
    }

    const sharedFilter = reconstructFiltersFromQuery(window.location.search);

    // to make correct api call on page load
    // eslint-disable-next-line max-len
    dispatch(updateRequestParams(preSelectedFiltersJson, sharedFilter, null, true));

    return preSelectedFiltersJson;
  };

  // for storing quickFilter's values
  const [selectedFilters, setSelectedFilters] = useState(getPreSelectedFilters);
  const [showAllFilters, setShowAllFilters] = useState(false);
  // for storing salary hike's values
  const [salaryHike, setSalaryHike] = useState([0, 900]);
  const [isSliderActive, setSliderActive] = useState(false);

  const visibleFiltersCount = window.innerHeight < 760 ? 2 : 6;

  const { quickFilters } = window.__INITIAL_STATE__;
  const requestParams = useSelector(requestParamsSelector) || {};

  const updateFiltersFn = useCallback((filters, type) => {
    gtmEventHandler(
      'cta',
      'salary_hike_filter',
      'click',
      'alumni_directory',
      {
        click_type: 'salary_hike_filter',
        click_section: `salary_hike_${type}`,
        click_text: `${filters.growth[0].min} - ${filters.growth[0].max}`,
      },
    );
    dispatch(
      updateRequestParams(
        [],
        filters,
        null,
      ),
    );
  }, [dispatch]);

  useEffect(() => {
    if (
      (
        Object.keys(requestParams.advancedFilters).length
          && !Object.keys(requestParams.advancedFilters).includes('growth')
      )
        || requestParams.search
    ) {
      setSalaryHike([0, 900]);
      setSelectedFilters([]);
    }
    if (requestParams.quickFilters.length > 0) {
      setSelectedFilters(requestParams.quickFilters);
    } else {
      setSelectedFilters([]);
    }

    if (!('growth' in requestParams.advancedFilters)) {
      setSalaryHike([0, 900]);
    }
    setSliderActive(false);
  }, [requestParams]);

  useEffect(() => {
    setPreSelectedFilters(selectedFilters);

    // handle dsml / review redirection
    if (selectedFilters.includes(DSML_QUICK_FILTER)) {
      if (!window.location.pathname.includes(DSML_URL_PATH)) {
        window.location.href = DSML_URL_PATH;
      }
    } else if (selectedFilters.includes(AIML_QUICK_FILTER)) {
      if (!window.location.pathname.includes(AIML_URL_PATH)) {
        window.location.href = AIML_URL_PATH;
      }
    } else if (!window.location.pathname.includes(REVIEW_URL_PATH)) {
      window.location.href = REVIEW_URL_PATH;
    }
  }, [selectedFilters]);

  const updateFilters = useCallback(debounce(updateFiltersFn, 1000), []);

  const handleSalaryHikeChange = useCallback(([min, max]) => {
    setSalaryHike([min, max]);
    setSelectedFilters([]);
    setSliderActive(true);
    updateFilters(
      {
        growth: [{ min, max }],
      },
      'slider',
    );
  }, [updateFilters]);

  const qnaWithAlumniFilters = quickFilters.filter(
    (filter) => filter.value === 'q&a_with_alumni',
  );

  const suggestedFilters = quickFilters.filter(
    filterItem => (!excludeQuickFilters.includes(filterItem.label)
    && filterItem.value !== 'q&a_with_alumni'),
  );

  const handleFilterChange = useCallback((event) => {
    const { value, checked } = event.target;
    setSelectedFilters((prevFilters) => {
      let updatedFilters = checked
        ? [...prevFilters, value]
        : prevFilters.filter((filter) => filter !== value);

      if (value === DSML_QUICK_FILTER) {
        updatedFilters = updatedFilters.filter(
          (filter) => filter !== AIML_QUICK_FILTER,
        );
      } else if (value === AIML_QUICK_FILTER) {
        updatedFilters = updatedFilters.filter(
          (filter) => filter !== DSML_QUICK_FILTER,
        );
      }

      dispatch(
        updateRequestParams(updatedFilters, {}, null),
      );

      return updatedFilters;
    });

    setSalaryHike([0, 900]);
  }, [dispatch]);

  const handleToggleFilters = useCallback(() => {
    setShowAllFilters((prevShowAllFilters) => !prevShowAllFilters);
  }, []);

  const visibleFilters = showAllFilters
    ? suggestedFilters
    : suggestedFilters.slice(0, visibleFiltersCount);

  const handleInputChange = useCallback((event) => {
    const { name, value } = event.target;
    const [min, max] = salaryHike;

    // Check if the input value is a valid number using isNaN
    const parsedValue = parseInt(value, 10);
    let updatedRange = [min, max];
    if (Number.isNaN(parsedValue)) {
      return;
    }

    if (name === 'min') {
      updatedRange = [Math.min(parsedValue, max), max];
      setSalaryHike(updatedRange);
    } else if (name === 'max') {
      updatedRange = [min, Math.max(parsedValue, min)];
      setSalaryHike(updatedRange);
    }
    setSelectedFilters([]);
    updateFilters(
      {
        growth: [{ min: updatedRange[0], max: updatedRange[1] }],
      },
      'input',
    );
  }, [salaryHike, updateFilters]);

  return (
    <div
      role="button"
      tabIndex={-1}
    >
      <div className={styles.title}>
        Quick Filters
      </div>
      <div>
        <div className={styles.newFilterContainer}>
          <div className={styles.filterHeading}>
            New filters
            <img
              className={styles.starPattern}
              src={doubleStarIcon}
              alt="stars"
            />
          </div>
          {qnaWithAlumniFilters.map((filter) => (
            <div className={styles.newFilterLine} key={filter.value}>
              <label className={styles.optionLabels}>
                <GTMTracking
                  component="input"
                  className={styles.checkBox}
                  type="checkbox"
                  value={filter.value}
                  checked={selectedFilters.includes(filter.value)}
                  onChange={handleFilterChange}
                  gtmEventType="cta"
                  gtmEventAction="click"
                  gtmEventResult="quick_filter_checkbox"
                  gtmEventCategory="alumni_directory"
                  gtmEventData={{
                    click_type: 'quick_filter_checkbox',
                    click_text: showAlumniConnect
                      ? filter.label : 'Q&A With Alumni',
                  }}
                />

                { showAlumniConnect ? filter.label : 'Q&A With Alumni'}
              </label>
              <img src={powerIcon} alt="powerIcon" />
            </div>
          ))}
        </div>
        <div className={styles.suggestedFilters}>
          <div className={styles.filterHeading}>
            Suggested Filters
          </div>
          <div className={styles.suggestedFiltersection}>
            {visibleFilters.map((filter) => (
              <label className={styles.optionLabels} key={filter.value}>
                <GTMTracking
                  component="input"
                  className={styles.checkBox}
                  type="checkbox"
                  value={filter.value}
                  checked={selectedFilters.includes(filter.value)}
                  onChange={handleFilterChange}
                  gtmEventType="cta"
                  gtmEventAction="click"
                  gtmEventResult="quick_filter_checkbox"
                  gtmEventCategory="alumni_directory"
                  gtmEventData={{
                    click_type: 'quick_filter_checkbox',
                    click_text: filter.label,
                  }}
                />
                {filter.label}
              </label>
            ))}
            {suggestedFilters.length > visibleFiltersCount && (
              <div
                className={styles.viewToggleFilter}
                onClick={handleToggleFilters}
                role="button"
                tabIndex={0}
              >
                {showAllFilters ? 'View Less Filters' : 'View More Filters'}
              </div>
            )}
          </div>
        </div>
        <div className={styles.lineBreaker} />
        <div className={styles.salaryHikeContainer}>
          <div className={styles.hikeFilterHeading}>
            Salary Hike Percentage
          </div>
          <div className={styles.hikeSubText}>
            Move the slider or type in
            the text box to get the filter by hike
          </div>
          <div className={styles.salaryHikeContainer}>
            <div className={styles.thumbIndicator}>
              {isSliderActive && (
                <>
                  <div
                    className={styles.indicatorBox}
                    style={
                      { left: `calc(${(salaryHike[0] / 900) * 100}% + 0.15rem` }
                    }
                  >
                    {`${salaryHike[0]}%`}
                    <div className={styles.arrow} />
                  </div>
                  <div
                    className={styles.indicatorBox}
                    style={
                      { left: `calc(${(salaryHike[1] / 900) * 100}% - 0.5rem` }
                    }
                  >
                    {`${salaryHike[1]}%`}
                    <div className={styles.arrow} />
                  </div>
                </>
              )}
            </div>
            <Slider
              className={styles.salaryHikeSlider}
              thumbClassName={styles.customThumb}
              trackClassName={styles.customTrack}
              value={salaryHike}
              onChange={handleSalaryHikeChange}
              min={0}
              max={900}
              step={10}
              pearling
              withBars
            />
          </div>

          <div className={styles.inputFieldsContainer}>
            <input
              type="text"
              name="min"
              value={`${salaryHike[0]}%`}
              onChange={handleInputChange}
              className={styles.inputField}
            />
            <span className={styles.dash}>-</span>
            <input
              type="text"
              name="max"
              value={`${salaryHike[1]}%`}
              onChange={handleInputChange}
              className={styles.inputField}
            />
          </div>
        </div>
      </div>
    </div>
  );
}

export default QuickFilters;
